import { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api, money, fmtDate } from "@/lib/api";
import { useTenant } from "@/context/TenantContext";
import { useAuth } from "@/context/AuthContext";
import StatusBadge, { statusColor } from "@/components/StatusBadge";
import { RecordForm } from "@/components/DataModule";
import JobDetail, { SlaBadge, SlaFields } from "@/components/JobDetail";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogContent, AlertDialogHeader, AlertDialogTitle, AlertDialogDescription as AlertDesc, AlertDialogFooter, AlertDialogCancel, AlertDialogAction } from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { toast } from "sonner";
import { Plus, Loader2, Table2, Columns3, Building2, MapPin, CalendarDays, User, X, Flag, Trash2 } from "lucide-react";

const OP_STATUSES = ["New", "Awaiting Review", "Awaiting Purchase Order", "Awaiting Materials", "Ready to Schedule", "Scheduled", "Assigned", "Technician En Route", "In Progress", "Paused", "Completed on Site", "Awaiting Documentation", "Awaiting Manager Approval", "Further Work Required", "Completed", "Closed", "Cancelled"];
const JOB_TYPES = ["Planned preventative maintenance", "Reactive call-out", "Emergency call-out", "Installation", "Remedial work", "Inspection", "Survey", "Quotation visit", "Project work", "Return visit"];
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const KANBAN = ["New", "Scheduled", "In Progress", "Completed on Site", "Completed"];
const initials = (name) => (name || "").split(" ").map((s) => s[0]).filter(Boolean).slice(0, 2).join("").toUpperCase();

function periodRange(period) {
  const now = new Date(); now.setHours(0, 0, 0, 0);
  if (period === "week") {
    const day = (now.getDay() + 6) % 7;
    const s = new Date(now); s.setDate(now.getDate() - day);
    const e = new Date(s); e.setDate(s.getDate() + 7);
    return [s, e];
  }
  if (period === "month") {
    const s = new Date(now.getFullYear(), now.getMonth(), 1);
    const e = new Date(now.getFullYear(), now.getMonth() + 1, 1);
    return [s, e];
  }
  if (period === "quarter") {
    const q = Math.floor(now.getMonth() / 3);
    const s = new Date(now.getFullYear(), q * 3, 1);
    const e = new Date(now.getFullYear(), q * 3 + 3, 1);
    return [s, e];
  }
  return null;
}
const PERIODS = [{ v: "all", l: "All" }, { v: "week", l: "This Week" }, { v: "month", l: "This Month" }, { v: "quarter", l: "This Quarter" }];

const JOB_FIELDS = [
  { key: "title", label: "Job title", required: true },
  { key: "customer_id", label: "Customer", type: "lookup", entity: "customers", labelKey: "name", mirror: "customer_name" },
  { key: "site_id", label: "Site", type: "lookup", entity: "sites", labelKey: "name", mirror: "site_name",
    dependsOn: { parent: "customer_id", attr: "customer_id", parentMirror: "customer_name", parentMirrorAttr: "customer_name", emptyText: "No sites for this customer" } },
  { key: "job_type", label: "Job type", type: "select", options: JOB_TYPES, default: "Reactive call-out" },
  { key: "priority", label: "Priority", type: "select", options: PRIORITIES, default: "Medium" },
  { key: "operational_status", label: "Operational status", type: "select", options: OP_STATUSES, default: "New" },
  { key: "scheduled_date", label: "Scheduled date", type: "date" },
  { key: "scheduled_time", label: "Scheduled time" },
  { key: "required_completion_date", label: "Required completion date", type: "date" },
  { key: "po_required", label: "PO required", type: "boolean" },
  { key: "po_number", label: "Customer PO number" },
  { key: "description", label: "Job description", type: "textarea" },
  { key: "customer_instructions", label: "Customer instructions", type: "textarea" },
];

export default function Jobs() {
  const { company } = useTenant();
  const { user } = useAuth();
  const isSuper = user?.role === "amden_super_admin";
  const cur = company?.currency || "EUR";
  const [jobs, setJobs] = useState(null);
  const [techs, setTechs] = useState([]);
  const [view, setView] = useState("table");
  const [period, setPeriod] = useState("all");
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({});
  const [detail, setDetail] = useState(null);
  const [saving, setSaving] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [initialForm, setInitialForm] = useState("{}");
  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const load = useCallback(() => {
    api.get("/jobs").then((r) => setJobs(r.data));
  }, []);
  useEffect(() => { load(); api.get("/technicians").then((r) => setTechs(r.data)); }, [load]);

  const [searchParams, setSearchParams] = useSearchParams();
  const tplFilter = searchParams.get("template");
  useEffect(() => {
    const openId = searchParams.get("open");
    if (openId && jobs) {
      const j = jobs.find((x) => x.id === openId);
      if (j) setDetail(j);
      else toast.error("That job no longer exists");
      setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete("open"); return n; }, { replace: true });
    }
  }, [jobs, searchParams, setSearchParams]);

  const openNew = () => {
    const def = {}; JOB_FIELDS.forEach((f) => { if (f.default !== undefined) def[f.key] = f.default; });
    setForm(def); setInitialForm(JSON.stringify(def)); setOpen(true);
  };

  const requestClose = () => {
    if (JSON.stringify(form) !== initialForm) setConfirmDiscard(true);
    else setOpen(false);
  };
  const create = async (override = false) => {
    if (!form.title) { toast.error("Job title required"); return; }
    setSaving(true);
    try {
      const body = override ? { ...form, sla_override: true } : form;
      await api.post("/jobs", body); toast.success("Job created"); setOpen(false); load();
    } catch (e) {
      if (e.response?.status === 409) {
        toast.error(e.response.data?.detail, { duration: 12000, action: { label: "Override & create", onClick: () => create(true) } });
      } else toast.error(e.response?.data?.detail || "Failed");
    } finally { setSaving(false); }
  };

  const hardDelete = async () => {
    if (!confirmDel) return;
    try {
      await api.delete(`/admin/records/jobs/${confirmDel.id}`);
      toast.success(`Job ${confirmDel.ref} deleted permanently`);
      setConfirmDel(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Delete failed"); }
  };

  const inPeriod = (j) => { const r = periodRange(period); if (!r) return true; const d = j.scheduled_date ? new Date(j.scheduled_date) : null; return !!d && d >= r[0] && d < r[1]; };
  const rangeActive = !!(from || to);
  const inRange = (j) => { const d = (j.scheduled_date || "").slice(0, 10); if (!d) return false; if (from && d < from) return false; if (to && d > to) return false; return true; };
  const shown = (jobs || []).filter((j) => !tplFilter || j.template_id === tplFilter).filter(rangeActive ? inRange : inPeriod);

  return (
    <div>
      <div className="flex items-center justify-between mb-4 gap-3 flex-wrap">
        <div>
          <h1 className="text-2xl font-head font-bold text-slate-900">{company?.terminology?.jobs || "Jobs"}</h1>
          <p className="text-sm text-slate-500">{jobs ? (tplFilter ? `${shown.length} of ${jobs.length} records` : rangeActive ? `${shown.length} of ${jobs.length} records · ${from || "…"} → ${to || "…"}` : period === "all" ? `${jobs.length} records` : `${shown.length} of ${jobs.length} records · ${PERIODS.find((p) => p.v === period)?.l}`) : "Loading…"}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {tplFilter && (
            <span data-testid="jobs-template-filter" className="inline-flex items-center gap-1.5 text-xs bg-slate-100 border border-slate-200 text-slate-700 rounded-full px-2.5 py-1">
              Visits of {searchParams.get("tref") || "recurring work"}
              <button data-testid="jobs-template-filter-clear" onClick={() => setSearchParams((prev) => { const n = new URLSearchParams(prev); n.delete("template"); n.delete("tref"); return n; }, { replace: true })} className="text-slate-400 hover:text-slate-700"><X className="h-3 w-3" /></button>
            </span>
          )}
          <div className="flex items-center gap-1.5 bg-white border border-slate-200 rounded-md px-2 h-9">
            <label className="text-xs text-slate-500">From</label>
            <input type="date" data-testid="jobs-from-date" value={from} onChange={(e) => setFrom(e.target.value)} className="text-sm text-slate-700 bg-transparent outline-none w-[120px]" />
            <span className="text-slate-300">–</span>
            <label className="text-xs text-slate-500">To</label>
            <input type="date" data-testid="jobs-to-date" value={to} onChange={(e) => setTo(e.target.value)} className="text-sm text-slate-700 bg-transparent outline-none w-[120px]" />
            {rangeActive && <button data-testid="jobs-range-clear" onClick={() => { setFrom(""); setTo(""); }} className="text-slate-400 hover:text-slate-700 ml-0.5"><X className="h-3.5 w-3.5" /></button>}
          </div>
          <Select value={period} onValueChange={setPeriod} disabled={rangeActive}>
            <SelectTrigger data-testid="jobs-period-filter" className="w-[150px] bg-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-white">{PERIODS.map((p) => <SelectItem key={p.v} value={p.v} data-testid={`jobs-period-${p.v}`}>{p.l}</SelectItem>)}</SelectContent>
          </Select>
          <Tabs value={view} onValueChange={setView}>
            <TabsList>
              <TabsTrigger value="table" data-testid="jobs-view-table"><Table2 className="h-4 w-4 mr-1" />Table</TabsTrigger>
              <TabsTrigger value="kanban" data-testid="jobs-view-kanban"><Columns3 className="h-4 w-4 mr-1" />Board</TabsTrigger>
            </TabsList>
          </Tabs>
          <Button data-testid="jobs-new-btn" className="btn-accent" onClick={openNew}><Plus className="h-4 w-4 mr-1" />New Job</Button>
        </div>
      </div>

      {view === "table" ? (
        <div className="bg-white border border-slate-200 rounded-md overflow-x-auto scroll-thin">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 sticky top-0">
              <tr>{["Job No", "Title", "Customer", "Site", "Type", "Priority", "Op Status", "Billing", "Tech", "Value"].map((h) => <th key={h} className="text-left font-medium text-slate-500 px-3 py-2.5 whitespace-nowrap">{h}</th>)}{isSuper && <th className="w-10"></th>}</tr>
            </thead>
            <tbody>
              {jobs === null && <tr><td colSpan={isSuper ? 11 : 10} className="text-center py-10 text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
              {jobs && shown.length === 0 && <tr><td colSpan={isSuper ? 11 : 10} className="text-center py-10 text-slate-400">No jobs in this period.</td></tr>}
              {jobs && shown.map((j) => (
                <tr key={j.id} data-testid={`job-row-${j.id}`} className="data-row border-t border-slate-100 cursor-pointer" onClick={() => setDetail(j)}>
                  <td className="px-3 py-2.5 font-medium text-slate-800 whitespace-nowrap">
                    <span className="inline-flex items-center gap-1.5">{j.ref}<SlaBadge job={j} />
                      {(j.flagged_notes || []).length > 0 && <Flag className="h-3.5 w-3.5 text-amber-500" data-testid={`flag-icon-${j.id}`} />}
                      {j.auto_billing && <span className="text-[9px] bg-purple-100 text-purple-700 font-semibold rounded px-1 py-0.5">AUTO</span>}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-slate-700">{j.title}</td>
                  <td className="px-3 py-2.5 text-slate-600">{j.customer_name}</td>
                  <td className="px-3 py-2.5 text-slate-600">{j.site_name}</td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{j.job_type}</td>
                  <td className="px-3 py-2.5"><StatusBadge status={j.priority} /></td>
                  <td className="px-3 py-2.5"><StatusBadge status={j.operational_status} /></td>
                  <td className="px-3 py-2.5"><StatusBadge status={j.billing_status} /></td>
                  <td className="px-3 py-2.5 text-slate-600 whitespace-nowrap">{j.technician_name || "—"}</td>
                  <td className="px-3 py-2.5 text-slate-700">{money(j.expected_value, cur)}</td>
                  {isSuper && (
                    <td className="px-2 py-2.5">
                      <button data-testid={`hard-delete-${j.id}`} title="Delete permanently (Super Admin)"
                        onClick={(e) => { e.stopPropagation(); setConfirmDel(j); }}
                        className="text-slate-300 hover:text-red-600 transition-colors p-1">
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="flex gap-4 overflow-x-auto scroll-thin pb-3 -mx-1 px-1">
          {KANBAN.map((col) => {
            const cc = statusColor(col);
            const colJobs = shown.filter((j) => j.operational_status === col);
            return (
              <div key={col} data-testid={`kanban-col-${col}`} className="w-[300px] flex-shrink-0 flex flex-col rounded-xl bg-slate-100/70 border border-slate-200/70 max-h-[calc(100vh-230px)]">
                <div className="rounded-t-xl" style={{ backgroundColor: cc.dot, height: 3 }} />
                <div className="px-3 py-2.5 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="h-2 w-2 rounded-full" style={{ backgroundColor: cc.dot }} />
                    <span className="text-sm font-head font-semibold text-slate-700">{col}</span>
                  </div>
                  <span className="text-xs font-medium text-slate-500 bg-white border border-slate-200 rounded-full px-2 py-0.5">{colJobs.length}</span>
                </div>
                <div className="px-2 pb-2 space-y-2 overflow-y-auto scroll-thin flex-1 min-h-[80px]">
                  {colJobs.length === 0 && <div className="text-center text-xs text-slate-400 py-8 border border-dashed border-slate-200 rounded-lg">No jobs</div>}
                  {colJobs.map((j) => {
                    const pc = statusColor(j.priority);
                    return (
                      <button key={j.id} data-testid={`kanban-card-${j.id}`} onClick={() => setDetail(j)}
                        className="w-full text-left bg-white rounded-lg border border-slate-200 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-150 overflow-hidden">
                        <div className="flex">
                          <span className="w-1 flex-shrink-0" style={{ backgroundColor: pc.dot }} />
                          <div className="flex-1 p-2.5 min-w-0">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-[11px] font-semibold accent-text inline-flex items-center gap-1">{j.ref}<SlaBadge job={j} /></span>
                              {j.billing_status && j.billing_status !== "Not Ready" && <StatusBadge status={j.billing_status} />}
                            </div>
                            <div className="text-sm font-medium text-slate-800 mt-0.5 line-clamp-2 leading-snug">{j.title}</div>
                            <div className="mt-1.5 space-y-0.5">
                              <div className="flex items-center gap-1.5 text-xs text-slate-500 truncate"><Building2 className="h-3 w-3 flex-shrink-0 text-slate-400" /><span className="truncate">{j.customer_name || "—"}</span></div>
                              {j.site_name && <div className="flex items-center gap-1.5 text-xs text-slate-500 truncate"><MapPin className="h-3 w-3 flex-shrink-0 text-slate-400" /><span className="truncate">{j.site_name}</span></div>}
                            </div>
                            <div className="mt-2 flex items-center justify-between gap-2">
                              <div className="flex items-center gap-1.5 min-w-0">
                                <StatusBadge status={j.priority} />
                                {j.scheduled_date && <span className="inline-flex items-center gap-1 text-[11px] text-slate-400 whitespace-nowrap"><CalendarDays className="h-3 w-3" />{fmtDate(j.scheduled_date)}</span>}
                              </div>
                              {j.technician_name
                                ? <span title={j.technician_name} className="h-6 w-6 flex-shrink-0 rounded-full grid place-items-center text-[10px] font-semibold text-white" style={{ backgroundColor: "var(--client-accent)" }}>{initials(j.technician_name)}</span>
                                : <span title="Unassigned" className="h-6 w-6 flex-shrink-0 rounded-full grid place-items-center text-slate-300 border border-dashed border-slate-300"><User className="h-3 w-3" /></span>}
                            </div>
                            {j.expected_value > 0 && <div className="mt-1.5 text-xs font-semibold text-slate-700">{money(j.expected_value, cur)}</div>}
                          </div>
                        </div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create dialog */}
      <Dialog open={open} onOpenChange={(o) => { if (!o) requestClose(); else setOpen(o); }}>
        <DialogContent onInteractOutside={(e) => e.preventDefault()} className="bg-white max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle className="font-head">New Job</DialogTitle>
            <DialogDescription className="text-xs text-slate-500">Pick a customer to narrow the site list, or pick a site to auto-fill the customer.</DialogDescription></DialogHeader>
          <RecordForm fields={JOB_FIELDS} value={form} onChange={setForm} />
          <SlaFields form={form} setForm={setForm} />
          <DialogFooter>
            <Button variant="outline" data-testid="job-create-cancel" onClick={requestClose}>Cancel</Button>
            <Button data-testid="job-create-save" className="btn-accent" onClick={() => create()} disabled={saving}>{saving && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}Create Job</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Detail */}
      {detail && <JobDetail job={detail} onClose={() => setDetail(null)} onChanged={load} techs={techs} cur={cur} />}

      <AlertDialog open={!!confirmDel} onOpenChange={(o) => !o && setConfirmDel(null)}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Permanently delete job {confirmDel?.ref}?</AlertDialogTitle>
            <AlertDesc>"{confirmDel?.title}" will be hard-deleted for good — it cannot be undone. An audit trail entry will be kept.</AlertDesc>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-hard-delete">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="confirm-hard-delete" className="bg-red-600 hover:bg-red-700 text-white" onClick={hardDelete}>Delete permanently</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={confirmDiscard} onOpenChange={setConfirmDiscard}>
        <AlertDialogContent className="bg-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Discard changes?</AlertDialogTitle>
            <AlertDesc>You have unsaved edits — closing will lose them.</AlertDesc>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="keep-editing-btn">Keep editing</AlertDialogCancel>
            <AlertDialogAction data-testid="discard-changes-btn" className="bg-red-600 hover:bg-red-700 text-white" onClick={() => { setConfirmDiscard(false); setOpen(false); }}>Discard</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
